/** Shapes served by the admin feature-flag API (`/admin/feature-flags`, src/routes/featureFlags.ts). */

export const FLAG_AREAS = [
  'shopify',
  'lightspeed',
  'bigcommerce',
  'integrations',
  'rebates',
  'pricing',
  'billing',
  'organizations',
  'catalog',
  'platform',
] as const;
export type FlagArea = (typeof FLAG_AREAS)[number];

export interface FlagOverrideEntry {
  organizationId: string;
  organizationName: string;
  organizationType: string | null;
  enabled: boolean;
}

/** A platform-wide setting: on, off, or on for plans that include the feature. */
export type GlobalSetting = boolean | 'plan';

export interface FeatureFlagDefinition {
  key: string;
  label: string;
  description: string;
  /** The description split into its template parts (src/types/featureFlags.ts). */
  on?: string;
  off?: string;
  commercialHome?: string;
  commercialHomeNote?: string;
  /** `planned` = a reserved key no code reads yet. */
  status: 'live' | 'planned';
  area?: FlagArea;
  /** Who the flag is for. A flag can carry more than one. */
  scopes?: Array<'store' | 'vendor' | 'system'>;
  /** ISO date (YYYY-MM-DD) the flag first landed on main. */
  addedOn?: string;
  defaultEnabled?: boolean;
  estimateRow?: string;
  issue?: number;
  /** Organizations with their own override, sorted by name and capped (see `overrideCount`). */
  overrides?: FlagOverrideEntry[];
  overrideCount?: number;
  /** How many of the `overrideCount` overrides are on. */
  overrideOnCount?: number;
  globalOverride?: GlobalSetting | null;
  /** Set when the flag's commercial home is a boolean plan feature; only then can it be set to `plan`. */
  planFeature?: string;
  /** Tiers whose seeded plan carries `planFeature`. */
  planTiers?: string[];
  /** Keys of the a la carte add-ons that unlock `planFeature`. */
  planAddons?: string[];
}

export interface RegistryResponse {
  definitions: FeatureFlagDefinition[];
  global: Record<string, GlobalSetting>;
}

export type FlagSource = 'default' | 'global' | 'plan' | 'organization';

export interface ResolvedFlagDetail {
  key: string;
  enabled: boolean;
  source: FlagSource;
}

export interface OrgOverridesResponse {
  overrides: Record<string, boolean>;
  resolved: {
    organizationId: string | null;
    flags: Record<string, boolean>;
    details: ResolvedFlagDetail[];
  };
}

/** The organization the scope bar is set to. */
export interface ScopedOrganization {
  id: string;
  name: string;
  type: string | null;
}

/** What the flag is for everyone with no override: the global setting, else the definition default. */
export const globalValueOf = (def: FeatureFlagDefinition, global: Record<string, GlobalSetting>): GlobalSetting => {
  const setting = global[def.key];
  if (setting === true || setting === false || setting === 'plan') return setting;
  return def.defaultEnabled === true;
};

/** Two-letter tile text for an organization name ("Harbor & Pine Cameras" -> "HP"). */
export const initialsOf = (name: string): string =>
  name
    .replace(/&/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

/** i18n key suffix under `admin.featureFlags.kinds` for an organization type. */
export const kindKeyOf = (type: string | null | undefined): 'camera_store' | 'vendor' | 'group' | 'other' =>
  type === 'camera_store' || type === 'vendor' || type === 'group' ? type : 'other';

/** The admin Organizations page opens on the organization named by `?org=`. */
export const organizationAdminPath = (organizationId: string) =>
  `/app/admin/brands-vendors/organizations?org=${encodeURIComponent(organizationId)}`;

export const ISSUE_URL_BASE = 'https://github.com/Motzu-Labs/camera-data-api/issues/';

/** Formats a YYYY-MM-DD date in the viewer's locale without a timezone shift. */
export const formatAddedOn = (iso: string | undefined, locale: string): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** True when the flag landed within the last `days` days (the "New" chip). */
export const isRecentlyAdded = (iso: string | undefined, now: Date = new Date(), days = 7): boolean => {
  if (!iso) return false;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return false;
  const added = new Date(y, m - 1, d).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return today - added >= 0 && today - added <= days * 86_400_000;
};
